import React from 'react'
import {
    forwardRef,
    useState,
    useCallback,
    useMemo,
    useRef,
    ReactNode,
} from 'react'
import {
    Box,
    Tabs,
    Tab,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    IconButton,
    MobileStepper,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faArrowRight,
    faExpand,
} from '@fortawesome/free-solid-svg-icons'
import { BaseBlockProps, Block } from '../types'
import { translateBlock } from '../../../utils'
import { BlockWrapper, BlockHeader, BlockToolbar } from '../base'
import { BlockDisplay } from './BlockDisplay'
import { CountListBlock } from './CountListBlock'
import { CountLinkBlock } from './CountLinkBlock'
import { SumFieldsBlock } from './SumFieldsBlock'
import { UserStatisticsBlock } from './UserStatisticsBlock'
import { DateStatisticsBlock } from './DateStatisticsBlock'
import { SupersetBlock } from './SupersetBlock'
import { PercentageMatchingBlock } from './PercentageMatchingBlock'
import { UserMostActiveStatisticsBlock } from './UserMostActiveStatisticsBlock'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface BlockContainerDisplayProps extends BaseBlockProps {}

type ContainerLayout = 'horizontal' | 'vertical' | 'carousel' | 'accordion'

export const BlockContainerDisplay = forwardRef<
    HTMLDivElement,
    BlockContainerDisplayProps
>(
    (
        {
            kedo,
            block,
            dashboard,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            misc,
        },
        ref
    ) => {
        const parRef = useRef<HTMLDivElement>(null)
        const [activeTab, setActiveTab] = useState(0)
        const [expandedAccordion, setExpandedAccordion] = useState<number>(0)
        const [showToolbar, setShowToolbar] = useState<number | null>(null)

        const layout =
            (block.settings?.layout as ContainerLayout) ?? 'horizontal'

        const sortedChildren = useMemo(() => {
            if (!block.children) return []
            return [...block.children].sort((a, b) => a.rank - b.rank)
        }, [block.children])

        const handleTabChange = useCallback(
            (_event: React.SyntheticEvent, newValue: number) => {
                setActiveTab(newValue)
            },
            []
        )

        const handleAccordionChange = useCallback(
            (index: number) =>
                (_event: React.SyntheticEvent, isExpanded: boolean) => {
                    setExpandedAccordion(isExpanded ? index : -1)
                },
            []
        )

        const handleCarouselNext = useCallback(() => {
            setActiveTab((prev) =>
                Math.min(prev + 1, sortedChildren.length - 1)
            )
        }, [sortedChildren.length])

        const handleCarouselBack = useCallback(() => {
            setActiveTab((prev) => Math.max(prev - 1, 0))
        }, [])

        const renderBlock = useCallback(
            (item: Block, settings?: Record<string, unknown>): ReactNode => {
                const miscSettings = settings ?? {}
                const blockProps = {
                    kedo,
                    block: item,
                    dashboard: true,
                    container: true,
                    dossier,
                    misc: miscSettings,
                }

                switch (item.type) {
                    case 'block_container':
                        return <BlockContainerDisplay {...blockProps} />
                    case 'dashboard':
                        return <BlockDisplay {...blockProps} />
                    case 'count_list_options':
                        return <CountListBlock {...blockProps} />
                    case 'list_option_percentage_conditions':
                        return <PercentageMatchingBlock {...blockProps} />
                    case 'count_link_options':
                        return <CountLinkBlock {...blockProps} />
                    case 'sum_fields':
                        return <SumFieldsBlock {...blockProps} />
                    case 'new_dossier_statistics':
                        return <DateStatisticsBlock {...blockProps} />
                    case 'active_users':
                        return <UserMostActiveStatisticsBlock {...blockProps} />
                    case 'online_users':
                        return <UserStatisticsBlock {...blockProps} />
                    case 'kedo_charts':
                        return <SupersetBlock {...blockProps} />
                    case 'date_statistics':
                        return <DateStatisticsBlock {...blockProps} />
                    default:
                        return null
                }
            },
            [kedo, dossier]
        )

        const renderHorizontalLayout = useCallback(() => {
            return (
                <>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        {sortedChildren.map((child, index) => (
                            <Tab
                                key={child.id}
                                label={translateBlock(child, kedo)}
                            />
                        ))}
                    </Tabs>
                    <Box
                        sx={{ pt: 2 }}
                        onMouseEnter={() =>
                            setShowToolbar(
                                sortedChildren[activeTab]?.id ?? null
                            )
                        }
                        onMouseLeave={() => setShowToolbar(block.id)}
                    >
                        {sortedChildren[activeTab] &&
                            renderBlock(sortedChildren[activeTab], {
                                hideTitle: true,
                            })}
                    </Box>
                </>
            )
        }, [
            activeTab,
            handleTabChange,
            sortedChildren,
            kedo,
            renderBlock,
            block.id,
        ])

        const renderVerticalLayout = useCallback(() => {
            return (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Tabs
                            orientation="vertical"
                            value={activeTab}
                            onChange={handleTabChange}
                            sx={{
                                borderRight: 1,
                                borderColor: 'divider',
                            }}
                        >
                            {sortedChildren.map((child, index) => (
                                <Tab
                                    key={child.id}
                                    label={translateBlock(child, kedo)}
                                />
                            ))}
                        </Tabs>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        <Box
                            onMouseEnter={() =>
                                setShowToolbar(
                                    sortedChildren[activeTab]?.id ?? null
                                )
                            }
                            onMouseLeave={() => setShowToolbar(block.id)}
                        >
                            {sortedChildren[activeTab] &&
                                renderBlock(sortedChildren[activeTab])}
                        </Box>
                    </Grid>
                </Grid>
            )
        }, [
            activeTab,
            handleTabChange,
            sortedChildren,
            kedo,
            renderBlock,
            block.id,
        ])

        const renderCarouselLayout = useCallback(() => {
            const currentChild = sortedChildren[activeTab]

            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100%-40px)',
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 500,
                            py: 0.5,
                            flexShrink: 0,
                        }}
                    >
                        {currentChild && translateBlock(currentChild, kedo)}
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            width: '100%',
                            '& > *': {
                                width: '100%',
                                height: '100%',
                            },
                        }}
                        onMouseEnter={() =>
                            setShowToolbar(currentChild?.id ?? null)
                        }
                        onMouseLeave={() => setShowToolbar(block.id)}
                    >
                        {currentChild && renderBlock(currentChild)}
                    </Box>
                    <MobileStepper
                        steps={sortedChildren.length}
                        position="static"
                        activeStep={activeTab}
                        sx={{
                            flexShrink: 0,
                            backgroundColor: 'transparent',
                            justifyContent: 'center',
                            gap: 2,
                            py: 0.5,
                            minHeight: 36,
                            position: 'fixed',
                            bottom: 0,
                            width: '100%',
                        }}
                        nextButton={
                            <IconButton
                                size="small"
                                onClick={handleCarouselNext}
                                disabled={
                                    activeTab === sortedChildren.length - 1
                                }
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.4,
                                    },
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowRight} />
                            </IconButton>
                        }
                        backButton={
                            <IconButton
                                size="small"
                                onClick={handleCarouselBack}
                                disabled={activeTab === 0}
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.4,
                                    },
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </IconButton>
                        }
                    />
                </Box>
            )
        }, [
            activeTab,
            sortedChildren,
            kedo,
            renderBlock,
            handleCarouselNext,
            handleCarouselBack,
            block.id,
        ])

        const renderAccordionLayout = useCallback(() => {
            return (
                <Box>
                    {sortedChildren.map((child, index) => (
                        <Accordion
                            key={child.id}
                            expanded={expandedAccordion === index}
                            onChange={handleAccordionChange(index)}
                        >
                            <AccordionSummary
                                expandIcon={<FontAwesomeIcon icon={faExpand} />}
                            >
                                <Typography>
                                    {translateBlock(child, kedo)}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                onMouseEnter={() => setShowToolbar(child.id)}
                                onMouseLeave={() => setShowToolbar(block.id)}
                            >
                                {expandedAccordion === index &&
                                    renderBlock(child)}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )
        }, [
            sortedChildren,
            expandedAccordion,
            handleAccordionChange,
            kedo,
            renderBlock,
            block.id,
        ])

        const renderLayout = useCallback(() => {
            switch (layout) {
                case 'vertical':
                    return renderVerticalLayout()
                case 'carousel':
                    return renderCarouselLayout()
                case 'accordion':
                    return renderAccordionLayout()
                default:
                    return renderHorizontalLayout()
            }
        }, [
            layout,
            renderVerticalLayout,
            renderCarouselLayout,
            renderAccordionLayout,
            renderHorizontalLayout,
        ])

        return (
            <BlockWrapper
                ref={ref}
                dashboard={dashboard}
                sx={{
                    marginBottom: '30px',
                    minHeight: '300px',
                    minWidth: '300px',
                }}
            >
                <Box
                    ref={parRef}
                    className={`kedo-block-container ${
                        !dashboard ? 'kedo-block' : ''
                    }`}
                    onMouseEnter={() => setShowToolbar(block.id)}
                    onMouseLeave={() => setShowToolbar(null)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <BlockHeader>
                        <BlockToolbar
                            kedo={kedo}
                            block={block}
                            onClose={onClose}
                            onMax={onMax}
                            onSuccess={onSuccess}
                        />
                        {getBlockHeader?.(block, dossier)}
                    </BlockHeader>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        {renderLayout()}
                    </Box>
                </Box>
            </BlockWrapper>
        )
    }
)

BlockContainerDisplay.displayName = 'BlockContainerDisplay'
